import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Add as AddIcon, SubdirectoryArrowRight } from "@mui/icons-material";
import { Layout } from "../components/Layout";
import {
  purchaseOrderService,
  PurchaseOrder,
} from "../services/purchaseOrder.service";
import { formatLocalDate } from "../utils/dateUtils";
import CreatePurchaseOrderModal from "../components/modals/CreatePurchaseOrderModal";

const PurchaseOrdersPage: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPurchaseOrders();
  }, []);

  const loadPurchaseOrders = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await purchaseOrderService.getPurchaseOrders();
      setPurchaseOrders(result.purchaseOrders);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusID: number) => {
    switch (statusID) {
      case 1:
        return "warning"; // Pending
      case 2:
        return "success"; // Complete
      case 3:
        return "error"; // On Hold
      case 4:
        return "info"; // Partial
      case 5:
        return "default"; // Building
      case 6:
        return "warning"; // Request: Pending
      case 7:
        return "success"; // Request: Complete
      default:
        return "default";
    }
  };

  const getPriorityColor = (priorityID?: number) => {
    switch (priorityID) {
      case 1:
        return "#c9892f"; // Low
      case 2:
        return "#569a45"; // Normal
      case 3:
        return "#c73e2e"; // High
      default:
        return "#569a45";
    }
  };

  return (
    <Layout>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant='h4'>Purchase Orders</Typography>
          <Box display='flex' gap={2}>
            <Button
              variant='outlined'
              onClick={() => navigate("/resupply/generate")}
            >
              Generate Resupply
            </Button>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}
            >
              Create PO
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : purchaseOrders.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>PO Date</TableCell>
                  <TableCell>Patients</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Requirements</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((po) => (
                  <TableRow key={po.id} hover>
                    <TableCell>{po.poNumber}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant='body2'>{po.clientName}</Typography>
                        {po.subClientName && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            <SubdirectoryArrowRight
                              sx={{
                                fontSize: 16,
                                mr: 0.5,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant='body2' color='text.secondary'>
                              {po.subClientName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatLocalDate(po.poDate)}</TableCell>
                    <TableCell>{po.patientCount || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={po.priorityName || "Normal"}
                        size='small'
                        sx={{
                          bgcolor: getPriorityColor(po.priorityID),
                          color: "white",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {po.signatureRequired && (
                          <Chip
                            label='Signature Required'
                            size='small'
                            color='info'
                          />
                        )}
                        {po.fromInventory && (
                          <Chip
                            label='From Inventory'
                            size='small'
                            color='secondary'
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          label={po.statusName}
                          color={getStatusColor(po.statusID)}
                          size='small'
                        />
                        {po.invoiced && (
                          <Chip label='Invoiced' size='small' color='success' />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color='text.secondary' gutterBottom>
              No purchase orders found
            </Typography>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}
              sx={{ mt: 2 }}
            >
              Create Your First PO
            </Button>
          </Paper>
        )}

        <CreatePurchaseOrderModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={(poId) => navigate(`/purchase-orders/${poId}/edit`)}
        />
      </Box>
    </Layout>
  );
};

export default PurchaseOrdersPage;
